import { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import TimeFilter from "../components/TimeFilter";
import MonthRangePicker from "../components/MonthRangePicker";
import StatCard from "../components/StatCard";
import DataTable from "../components/DataTable";
import DashboardSection from "../components/DashboardSection";
import ErrorState from "../components/ErrorState";
import { SkeletonStatGrid, SkeletonTable } from "../components/Skeleton";
import { ReceiptIcon, TrendUpIcon, ClockIcon } from "../components/icons";

import {
  transactionsToday,
  transactionsYesterday,
  transactionsLast7,
  transactionsLast30,
  transactionsMonthlyRange
} from "../api/transaction.api";

export default function Transactions() {
  const [filter, setFilter] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filter !== "custom") loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (filter === "today") res = await transactionsToday();
      else if (filter === "yesterday") res = await transactionsYesterday();
      else if (filter === "last7") res = await transactionsLast7();
      else if (filter === "last30") res = await transactionsLast30();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const loadCustom = async ({ fromYear, fromMonth, toYear, toMonth }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await transactionsMonthlyRange({
        start_year: fromYear,
        start_month: fromMonth,
        end_year: toYear,
        end_month: toMonth
      });
      const dataRows = res.data?.data || res.data;
      setRows(Array.isArray(dataRows) ? dataRows : []);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { count: 0, amount: 0, extension: 0 };
    }
    return rows.reduce(
      (a, r) => {
        a.count += typeof r.count === "number" && !Number.isNaN(r.count) ? r.count : 0;
        a.amount += typeof r.loan_amount_total === "number" && !Number.isNaN(r.loan_amount_total) ? r.loan_amount_total : 0;
        a.extension += typeof r.extension_charge_total === "number" && !Number.isNaN(r.extension_charge_total) ? r.extension_charge_total : 0;
        return a;
      },
      { count: 0, amount: 0, extension: 0 }
    );
  }, [rows]);

  return (
    <div className="page-container">
      <PageHeader
        title="Transactions & Volume"
        subtitle="Volume, loan disbursements, repayments and extension charges by type."
      />

      <div className="toolbar">
        <TimeFilter value={filter} onChange={setFilter} />
      </div>
      <MonthRangePicker open={filter === "custom"} onApply={loadCustom} />

      {error ? (
        <ErrorState message={error} onRetry={filter === "custom" ? undefined : loadData} />
      ) : (
        <>
          <DashboardSection title="Transaction Totals">
            {loading ? (
              <SkeletonStatGrid count={3} />
            ) : (
              <div className="grid">
                <StatCard
                  title="Total Transactions"
                  value={summary.count}
                  icon={ReceiptIcon}
                  index={0}
                  tone="accent"
                />
                <StatCard
                  title="Total Loan Amount"
                  value={summary.amount}
                  isCurrency={true}
                  icon={TrendUpIcon}
                  index={1}
                  tone="accent"
                />
                <StatCard
                  title="Total Extension Charges"
                  value={summary.extension}
                  isCurrency={true}
                  icon={ClockIcon}
                  index={2}
                  tone="warn"
                />
              </div>
            )}
          </DashboardSection>

          <DashboardSection title="Transaction Breakdown Table">
            {loading ? (
              <SkeletonTable rows={4} />
            ) : (
              <DataTable
                columns={[
                  "transaction_type",
                  "count",
                  "loan_amount_total",
                  "extension_charge_total"
                ]}
                data={rows}
              />
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
